import React from 'react';
import { Star } from 'lucide-react';
import AIEssentialsBundleCard from '@/components/cards/homepage/AIEssentialsBundleCard';
import { useFeaturedPricing } from '@/hooks/useFeaturedPricing';

/**
 * AIEssentialsBundlesSection Component
 *
 * Displays the bundles section showcasing course bundle offerings.
 * Uses reusable bundle card components for consistent design and maintainability.
 *
 * Pricing is dynamically fetched from the backend pricing API to ensure
 * consistency with the admin panel settings.
 *
 * @component
 * @example
 * ```tsx
 * <AIEssentialsBundlesSection />
 * ```
 */
export default function AIEssentialsBundlesSection() {
  // Fetch pricing for all featured products in a single API call
  const { bundlePricing, loading, isSaleActive } = useFeaturedPricing();

  // Dynamic savings percentage from API
  const savingsPercentage = bundlePricing?.savings_percentage ?? 0;

  return (
    <section className="py-24 px-6 bg-gradient-to-br from-purple-900/10 via-black to-blue-900/10 relative overflow-hidden">
      <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.15) 0%, transparent 60%)',
      }}></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-6 py-2 bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-cyan-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
            <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent font-semibold">
              {isSaleActive && savingsPercentage > 0
                ? `💎 BEST VALUE • SAVE ${savingsPercentage}%`
                : '💎 BEST VALUE BUNDLE'
              }
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="block">Get Both Courses</span>
            <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              In Our Starter Bundle
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {isSaleActive && bundlePricing?.savings_amount
              ? `The perfect foundation for anyone serious about mastering AI - Save $${Math.round(bundlePricing.savings_amount)} when you bundle`
              : 'The perfect foundation for anyone serious about mastering AI'
            }
          </p>
        </div>

        {/* Bundle Card */}
        <AIEssentialsBundleCard pricing={bundlePricing} loading={loading} />

        {/* Social Proof */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 px-6 py-4 rounded-full">
            <div className="flex -space-x-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 border-2 border-black"></div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 border-2 border-black"></div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-400 border-2 border-black"></div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-400 border-2 border-black"></div>
            </div>
            <div className="text-left">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-sm text-gray-400">
                Join our <span className="text-white font-semibold">growing community</span> mastering AI
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
